import { useCallback, useSyncExternalStore } from 'react';

/** Tailwind 기본 브레이크포인트와 정렬 — md=768, lg=1024 */
export const BP_MD = 768;
export const BP_LG = 1024;

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

const QUERY_TABLET_UP = `(min-width: ${BP_MD}px)`;
const QUERY_DESKTOP_UP = `(min-width: ${BP_LG}px)`;

/**
 * media query 구독 — useSyncExternalStore 기반이라 tearing 없이 일치하는 값만 리렌더.
 *
 * resize 리스너로 window.innerWidth 를 추적하면 브레이크포인트가 안 바뀌어도 매 프레임 갱신되므로
 * 반응형 판정에는 항상 이 훅을 쓴다.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );
  const getSnapshot = useCallback(
    () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false),
    [query],
  );
  // SSR/프리렌더에는 데스크톱 가정 (기존 useViewport 기본값과 동일)
  return useSyncExternalStore(subscribe, getSnapshot, () => true);
}

/**
 * 앱 공용 브레이크포인트 — mobile <768 / tablet 768~1023 / desktop >=1024.
 *
 * 레이아웃 분기(테이블 ↔ 카드, 2단 분할 해제 등)의 단일 소스.
 */
export function useBreakpoint(): {
  bp: Breakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  /** lg 미만 — 2단 분할을 세로 스택으로 내리는 판정 */
  isBelowLg: boolean;
} {
  const tabletUp = useMediaQuery(QUERY_TABLET_UP);
  const desktopUp = useMediaQuery(QUERY_DESKTOP_UP);

  const bp: Breakpoint = !tabletUp ? 'mobile' : !desktopUp ? 'tablet' : 'desktop';
  return {
    bp,
    isMobile: bp === 'mobile',
    isTablet: bp === 'tablet',
    isDesktop: bp === 'desktop',
    isBelowLg: !desktopUp,
  };
}

export default useBreakpoint;
